import type { Substance } from '../types';

export const alcohol: Substance = {
  id: 'alcohol',
  displayName: 'Alcohol (Ethanol)',
  emoji: '🍷',
  aliases: ['alcohol', 'ethanol', 'booze', 'beer', 'wine'],
  substanceClass: 'depressant',
  mechanismSummary:
    'Enhances inhibitory GABA-A signalling and dampens excitatory NMDA signalling, producing widespread CNS ' +
    'depression. Unlike drugs with one saturable site, ethanol acts across a broad millimolar range with no ' +
    'classic nanomolar affinity — and its elimination is roughly CONSTANT-rate rather than a fixed ' +
    'half-life, so it uses a different underlying model from the other substances here.',
  pk: {
    route: 'zero-order-elimination',
    ka: 1.8,
    vd: 42,
    bioavailability: 0.9,
    molecularWeightGMol: 46.07,
    brainPlasmaRatio: 1.0,
    eliminationRateMgPerLPerHour: 175,
  },
  doseRange: { min: 7000, max: 56000, default: 14000, step: 1000, unit: 'g', displayScale: 0.001 },
  bindings: [
    {
      receptor: 'GABA_A',
      kiNM: 15_000_000,
      action: 'positive-modulator',
      citation:
        'Functional EC50-style approximation, not a classic Ki — ethanol potentiates GABA-A across ~3-50 mM ' +
        'depending on subunit composition. Mihic SJ et al. (1997) Nature 389:385-389; Wallner M, Hanchar HJ, ' +
        'Olsen RW (2003) PNAS 100:15218-15223.',
    },
    {
      receptor: 'NMDA',
      kiNM: 30_000_000,
      action: 'negative-modulator',
      citation:
        'Functional approximation from Lovinger DM, White G, Weight FF (1989) Science 243:1721-1724. ' +
        'PMID: 2467382 (50 mM produced 61% current inhibition; not a formally derived IC50).',
    },
  ],
  colorPalette: { primary: '#8855cc', secondary: '#6644aa', particleSpeedMultiplier: 0.5 },
};

export const diazepam: Substance = {
  id: 'diazepam',
  displayName: 'Diazepam',
  emoji: '😌',
  aliases: ['diazepam', 'valium', 'benzo', 'benzodiazepine'],
  substanceClass: 'depressant',
  mechanismSummary:
    'A benzodiazepine: it binds a distinct site on the GABA-A receptor and makes the channel respond more ' +
    'strongly to GABA already present. Because it amplifies existing signalling rather than opening the ' +
    'channel itself, it has a ceiling on its own — but that ceiling disappears when combined with alcohol ' +
    'or opioids, which is what makes those combinations dangerous.',
  pk: {
    route: 'oral',
    ka: 1.4,
    ke: 0.0173,
    vd: 80,
    bioavailability: 0.94,
    molecularWeightGMol: 284.74,
    brainPlasmaRatio: 1.1,
  },
  doseRange: { min: 2, max: 30, default: 5, step: 1, unit: 'mg', displayScale: 1 },
  bindings: [
    {
      receptor: 'GABA_A',
      kiNM: 15,
      action: 'positive-modulator',
      citation:
        'Approximate benzodiazepine-site affinity; commonly cited in the ~10-20 nM range. ' +
        'NIMH PDSP Ki Database, https://pdsp.unc.edu/databases/kidb.php.',
    },
  ],
  colorPalette: { primary: '#7799dd', secondary: '#5566aa', particleSpeedMultiplier: 0.45 },
};

export const melatonin: Substance = {
  id: 'melatonin',
  displayName: 'Melatonin',
  emoji: '🌙',
  aliases: ['melatonin'],
  substanceClass: 'depressant',
  mechanismSummary:
    'Agonist at MT1 and MT2 receptors in the hypothalamus, suppressing wake-promoting firing and shifting ' +
    'circadian timing. Oral bioavailability is unusually variable between individuals — real pharmacokinetic ' +
    'variability, not a gap in the model.',
  pk: {
    route: 'oral',
    ka: 2.5,
    ke: 0.833,
    vd: 38.5,
    bioavailability: 0.2,
    molecularWeightGMol: 232.28,
    brainPlasmaRatio: 0.9,
  },
  doseRange: { min: 0.5, max: 10, default: 3, step: 0.5, unit: 'mg', displayScale: 1 },
  bindings: [
    {
      receptor: 'MT1',
      kiNM: 0.1,
      action: 'agonist',
      citation:
        'Sub-nanomolar, ~20-200 pM reported range. Reppert SM, Weaver DR, Ebisawa T (1994) Neuron ' +
        '13:1177-1185. PMID: 7946350; Browning C et al. (2000) PMC1571913.',
    },
    {
      receptor: 'MT2',
      kiNM: 0.3,
      action: 'agonist',
      citation:
        'Sub-nanomolar, ~0.1-0.5 nM, generally lower affinity than MT1. Reppert SM et al. (1995) PNAS ' +
        '92:8734-8738. PMID: 7568007.',
    },
  ],
  colorPalette: { primary: '#3355aa', secondary: '#224488', particleSpeedMultiplier: 0.4 },
};

export const DEPRESSANTS = [alcohol, diazepam, melatonin];
